/**
 * Authenticated API request helper.
 * UI side-effects (notify / redirect) go through appBridge — never import main.js.
 */
import axios from 'axios'
import client from './client'
import { notify, getRoute, getRouter } from './appBridge'
import { getSelectedGroupId, getAbortController } from './session'

// Wired from services after construction (avoids circular imports)
let _authLogout = null
let _ensureSelectedGroup = null

export function setAuthLogout(fn) {
  _authLogout = fn
}

/** Called when selected group id is missing — usually storage.selectedGroup() */
export function setEnsureSelectedGroup(fn) {
  _ensureSelectedGroup = fn
}

function clearAuthStorage() {
  localStorage.removeItem('loggedIn')
  localStorage.removeItem('user')
}

function redirectHome() {
  window.location.replace('/')
}

/**
 * @param {string} url
 * @param {string} method
 * @param {object} [data]
 * @param {string[]} [urlParams]
 * @param {object} [headers]
 * @param {string} [responseType]
 */
export function request(url, method, data, urlParams, headers, responseType = 'json') {
  return new Promise((resolve, reject) => {
    if (!getSelectedGroupId() && _ensureSelectedGroup) {
      _ensureSelectedGroup()
    }
    const selectedGroupId = getSelectedGroupId()

    if (urlParams && urlParams.length > 0) {
      url = url.indexOf('?') >= 0
        ? `${url}&${urlParams.join('&')}`
        : `${url}?${urlParams.join('&')}`
    }
    headers = headers || {}

    let body
    if (method.toUpperCase() === 'GET') {
      if (selectedGroupId) {
        url = url.indexOf('?') >= 0
          ? `${url}&selected_group_id=${selectedGroupId}`
          : `${url}?selected_group_id=${selectedGroupId}`
      }
    } else {
      body = { selected_group_id: selectedGroupId, signal: getAbortController().signal }
      if (data) {
        if (typeof FormData !== 'undefined' && data instanceof FormData) {
          if (selectedGroupId && !data.has('selected_group_id')) {
            data.append('selected_group_id', selectedGroupId)
          }
          body = data
        } else if (
          url.indexOf('file/upload') >= 0 ||
          (url.indexOf('newsAttachment') >= 0 && method.toUpperCase() === 'POST')
        ) {
          body = data
        } else {
          body = {
            selected_group_id: selectedGroupId,
            ...data,
          }
        }
      }
    }

    const config = {
      method,
      url,
      withCredentials: true,
      headers: { ...headers },
      responseType,
      data: body,
    }

    for (const key in config.headers) {
      if (!config.headers[key]) {
        delete config.headers[key]
      }
    }

    client(config)
      .then((result) => resolve(result))
      .catch(function handleError(error) {
        if (axios.isCancel(error) || error?.code === 'ERR_CANCELED') {
          console.error('Operation canceled')
          return
        }

        const status = error?.response?.status
        const message = error?.response?.data?.message

        if (status === 401) {
          clearAuthStorage()
          if (getRoute().path !== '/') redirectHome()
          reject(error)
          return
        }

        if (status === 403) {
          notify({
            type: 'warn',
            title: 'Sem permissão para acessar esse recurso',
            text: message?.replace?.('[BR]', '') || '',
          })
          reject(false)
          return
        }

        if (status === 419 || message === 'Unauthenticated.') {
          if (error?.config?.url?.endsWith('/logout')) {
            clearAuthStorage()
            if (getRoute().path !== '/') redirectHome()
            reject(error)
            return
          }
          const logout = _authLogout
          if (logout) {
            logout()
              .then(() => redirectHome())
              .catch(() => redirectHome())
          } else {
            clearAuthStorage()
            redirectHome()
          }
          return
        }

        if (status === 429) {
          notify({
            type: 'error',
            text: 'Servidor ocupado. Aguarde alguns instantes.',
          })
          return
        }

        if (status === 503) {
          notify({
            type: 'warn',
            text: 'Servidor em manutenção. Aguarde alguns instantes.',
            duration: 500,
          })
          setTimeout(() => {
            let returnUrl = encodeURIComponent(getRoute().path)
            if (returnUrl === '/maintenance') {
              if (getRoute()?.query?.returnUrl) {
                returnUrl = getRoute().query.returnUrl
              } else {
                returnUrl = encodeURIComponent('/')
              }
            }
            getRouter()?.push({ path: 'maintenance', query: { returnUrl } })
          }, 600)
          return
        }

        if (message && message.indexOf('[BR]') === 0) {
          notify({ type: 'error', text: message.replace('[BR]', '') })
          reject(new Error(''))
          return
        }

        if (message && message.indexOf('[EN]') === 0) {
          notify({
            type: 'error',
            text: message.replace('[EN]', 'TRADUZIDO: '),
          })
          reject(new Error(''))
          return
        }

        reject(error)
      })
  })
}

/**
 * Unauthenticated call to a third-party origin (e.g. BrasilAPI).
 * Must not use the Sanctum client: withCredentials + ACAO `*` is blocked by browsers.
 */
export function requestOutside(url, method, data, urlParams, headers) {
  return new Promise((resolve, reject) => {
    if (urlParams?.length) {
      url = url.indexOf('?') >= 0
        ? `${url}&${urlParams.join('&')}`
        : `${url}?${urlParams.join('&')}`
    }
    headers = headers || {}
    axios({
      method,
      url,
      withCredentials: false,
      headers: { ...headers },
      data,
    })
      .then((result) => resolve(result))
      .catch((error) => reject(error))
  })
}

